import { useSyncExternalStore } from 'react';

/// What somebody chose. "System" is not a third colour scheme - it is the absence of a
/// choice, and it follows the desktop, which is what most people actually want and what the
/// apps embedding this interface in a WebView already do.
export type Appearance = 'system' | 'light' | 'dark';

/// What the interface is actually painted in, once the choice has been resolved.
export type ColourMode = 'light' | 'dark';

/// Kept on the device rather than on the user: the same person on a television in the evening
/// and on a laptop by a window does not want one answer for both. It is also why this never
/// waits for a sign-in - the sign-in screen is already in the right colours.
const STORAGE_KEY = 'emiro.appearance';

const DARK_QUERY = '(prefers-color-scheme: dark)';

const listeners = new Set<() => void>();

let chosen: Appearance = read();

function read(): Appearance {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);

    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    // A browser that refuses storage still gets an interface, it just forgets the choice.
    return 'system';
  }
}

function systemMode(): ColourMode {
  return typeof window !== 'undefined' && window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

/// The choice and what it currently resolves to, as one string, so React can compare snapshots
/// without an object being rebuilt on every read.
function snapshot(): string {
  return `${chosen}:${systemMode()}`;
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);

  const media = window.matchMedia(DARK_QUERY);

  // Two things move this: the desktop switching to night, and another tab of the same server
  // being told to change.
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) {
      chosen = read();
      onChange();
    }
  };

  media.addEventListener('change', onChange);
  window.addEventListener('storage', onStorage);

  return () => {
    listeners.delete(onChange);
    media.removeEventListener('change', onChange);
    window.removeEventListener('storage', onStorage);
  };
}

export function setAppearance(next: Appearance): void {
  chosen = next;

  try {
    if (next === 'system') {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, next);
    }
  } catch {
    // The choice holds for this session even when it cannot be written down.
  }

  for (const listener of [...listeners]) {
    listener();
  }
}

export function useAppearance(): { appearance: Appearance; mode: ColourMode } {
  const [appearance, system] = useSyncExternalStore(subscribe, snapshot).split(':');

  return {
    appearance: appearance as Appearance,
    mode: appearance === 'system' ? (system as ColourMode) : (appearance as ColourMode),
  };
}
